import { Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/core/lib/utils';
import { useUIStore } from '@/core/stores/ui.store';
import { useDateFormat } from '@/shared/hooks/useDateFormat';
import type { PlannedDate } from '@/shared/types/datetime';
import type { TaskStatusType } from '../types';
import {
  addDays,
  formatPlannedDate,
  formatPlannedTime,
  shortTimeZoneLabel,
} from '@/shared/lib/datetime/planned-date';

interface DateDisplayProps {
  date: PlannedDate | null | undefined;
  /** Semantic role of this date — a start date is never an urgency signal. */
  variant: 'start' | 'due';
  statusType: TaskStatusType;
  fallback?: string;
  className?: string;
  showIcon?: boolean;
}

type DateTone = 'error' | 'warning' | 'success' | 'neutral';

const TONE_CLASS: Record<DateTone, string> = {
  error: 'text-chart-5',
  warning: 'text-warning',
  success: 'text-success',
  neutral: 'text-muted-foreground',
};

/** Today's calendar day (`yyyy-MM-dd`) in the user's configured timezone. */
function todayInTz(timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/**
 * Read-only display for a `PlannedDate`.
 *
 * Floating values are compared and formatted by calendar day — no instant math,
 * no timezone conversion — so "today/tomorrow/yesterday" and overdue are computed
 * against the user's local calendar day without the old off-by-one.
 */
export function DateDisplay({
  date,
  variant,
  statusType,
  fallback,
  className,
  showIcon,
}: DateDisplayProps) {
  const { t } = useTranslation('tasks');
  const { dateFormat, timeFormat } = useDateFormat();
  const timezone = useUIStore((s) => s.timezone);

  if (!date) {
    return (
      <span className={cn('text-xs text-muted-foreground', className)}>
        {fallback ?? t('listView.noDate')}
      </span>
    );
  }

  const today = todayInTz(timezone);
  const tomorrow = addDays({ date: today }, 1).date;
  const yesterday = addDays({ date: today }, -1).date;

  const timeSuffix = date.time != null ? `, ${formatPlannedTime(date, timeFormat)}` : '';
  const zoneSuffix = date.timeZoneId != null ? ` (${shortTimeZoneLabel(date.timeZoneId)})` : '';

  let base: string;

  if (date.date === today) {
    base = t('listView.today');
  } else if (date.date === tomorrow) {
    base = t('listView.tomorrow');
  } else if (date.date === yesterday) {
    base = t('listView.yesterday');
  } else {
    base = formatPlannedDate({ date: date.date }, { dateFormat, timeFormat });
  }

  // A completed task's dates are historical, not a live urgency signal — always
  // success, regardless of role or how far past/future the date sits. A start
  // date alone never carries urgency; only a due date can be "late"/"approaching".
  let tone: DateTone;
  if (statusType === 'done') {
    tone = 'success';
  } else if (variant === 'start') {
    tone = 'neutral';
  } else if (date.date < today) {
    tone = 'error';
  } else if (date.date === today || date.date === tomorrow) {
    tone = 'warning';
  } else {
    tone = 'neutral';
  }

  const label = `${base}${timeSuffix}${zoneSuffix}`;

  return (
    <span
      className={cn('text-xs', showIcon && 'flex items-center gap-1', TONE_CLASS[tone], className)}
    >
      {showIcon && <Calendar className="size-3 shrink-0" />}
      {label}
    </span>
  );
}
